"use client"

import { useSyncExternalStore } from "react"

import { useFlagExposure, useSilentFlagVariant } from "@/hooks/use-experiment-flag"
import type { ProductRecommendation } from "@/data/guide-product-recommendations"
import { GuideSidebarLedger } from "@/components/guides/guide-sidebar-ledger"
import { GuideSidebarProduct } from "@/components/guides/guide-sidebar-product"

/** Multivariate flag: variants `control` and `inline`. */
export const GUIDE_RAIL_FLAG = "guide_affiliate_rail_v1"

/**
 * NEW surface key for the test arm. Deliberately distinct from both
 * `guide-sidebar` (the control rail, shared with the blog rails) and
 * `guide-<slug>` (the in-body picks cards), so the read can attribute the arm
 * without untangling either historical series.
 */
export const GUIDE_RAIL_INLINE_SURFACE = "guide-sidebar-inline"

/** Viewport where the guide page shows its right rail. */
export const GUIDE_RAIL_MEDIA_QUERY = "(min-width: 1200px)"

function subscribeRailViewport(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {}
  const mql = window.matchMedia(GUIDE_RAIL_MEDIA_QUERY)
  mql.addEventListener("change", onChange)
  return () => mql.removeEventListener("change", onChange)
}
function railViewportMatches(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(GUIDE_RAIL_MEDIA_QUERY).matches
  )
}
const railViewportServer = () => false

interface GuideSidebarRailExperimentProps {
  slug: string
  products: ProductRecommendation[]
}

/**
 * guide_affiliate_rail_v1: in the `inline` arm the guide right rail renders the
 * quiet-ledger rail (GuideSidebarLedger, ranked rows with text links) in place
 * of the compact rail. Only the literal `inline` variant changes anything; every
 * other value, including `undefined` before the flag resolves and off-prod where
 * PostHog is disabled, falls through to today's rail.
 *
 * This wrapper exists so the flag read (and therefore the $feature_flag_called
 * exposure that defines the eligible population) is only ever made from guide
 * pages. The blog rails import GuideSidebarProduct directly and stay out of the
 * experiment entirely.
 *
 * The rail's <aside> is display:none outside GUIDE_RAIL_MEDIA_QUERY (the guide
 * page's `min-[1200px]:block`), so exposure is sent only when the viewport can
 * show the rail and there are picks to render.
 */
export function GuideSidebarRailExperiment({ slug, products }: GuideSidebarRailExperimentProps) {
  const variant = useSilentFlagVariant(GUIDE_RAIL_FLAG)
  const railVisible = useSyncExternalStore(subscribeRailViewport, railViewportMatches, railViewportServer)
  useFlagExposure(GUIDE_RAIL_FLAG, variant !== undefined && railVisible && products.length > 0)
  const isInline = variant === "inline"

  // "unresolved" rather than "control" while the flag has not answered: stamping
  // control there would mislabel viewers whose assignment lands on `inline` a
  // beat later. Any other non-inline string is a control fall-through.
  const railVariant = isInline ? "inline" : variant === undefined ? "unresolved" : "control"

  if (isInline) {
    return (
      <GuideSidebarLedger
        slug={slug}
        products={products}
        surface={GUIDE_RAIL_INLINE_SURFACE}
        railVariant="inline"
        sourcePrefix="guide-sidebar"
      />
    )
  }

  return (
    <GuideSidebarProduct
      slug={slug}
      products={products}
      sourcePrefix="guide-sidebar"
      railVariant={railVariant}
    />
  )
}
